import React from "react";

const Documentation = () => {
  return (
    <div className="max-w-4xl mx-auto py-8 px-4">
      <h1 className="text-2xl font-bold text-gray-900 mb-2">Documentation</h1>
      <p className="text-sm text-gray-500 mb-8">Internal reference for data model concepts and conventions.</p>

      {/* Organisation */}
      <Section
        title="Organisation"
        subtitle="Legal / Financial entity"
        field="organisation_id + organisation_name"
        color="blue">
        <p>
          The <strong>real company</strong> that employs and pays the person. This is the entity that appears on contracts, invoices, payslips, and all
          financial/legal documents.
        </p>
        <Values
          items={[
            { value: "Selego BV", desc: "Dutch entity (Netherlands)" },
            { value: "Le Collectif", desc: "French entity (France)" },
            { value: "Selegoloco", desc: "Spanish entity (Spain)" },
            { value: "Walego", desc: "Walego the company" },
          ]}
        />
        <h4 className="font-medium text-gray-700 mt-4 mb-2">Determines:</h4>
        <ul className="list-disc list-inside text-sm text-gray-600 space-y-1">
          <li>Employment contract and legal framework</li>
          <li>IBAN on invoices</li>
          <li>VAT rate (FR 20%, NL 21%, ES 20%)</li>
          <li>Salary calculation rules</li>
          <li>Weeks off (FR = 10 weeks, others = 5 weeks)</li>
          <li>Tax reports eligibility (WBSO for NL, CIR for FR)</li>
        </ul>
        <Callout>A person's organisation is about who signs their paycheck, not where they spend their time.</Callout>
      </Section>

      {/* Business Unit */}
      <Section
        title="Business Unit"
        subtitle="Internal / Resource management"
        field="business_unit"
        color="purple">
        <p>
          The <strong>internal business pole</strong> the person works for day-to-day. Used for resource management, staffing, and internal cost allocation.
        </p>
        <Values
          items={[
            { value: "SELEGO", desc: "Consulting / professional services (includes Selego BV + Le Collectif people)" },
            { value: "TIRANA", desc: "Tirana startup / innovation pole" },
            { value: "WALEGO", desc: "AI product (includes Walego + Selegoloco people)" },
          ]}
        />
        <h4 className="font-medium text-gray-700 mt-4 mb-2">Used for:</h4>
        <ul className="list-disc list-inside text-sm text-gray-600 space-y-1">
          <li>Resource planning and staffing</li>
          <li>Internal cost allocation between poles</li>
          <li>TACE and occupation dashboards</li>
          <li>Team management and filtering</li>
        </ul>
        <Callout>A person's business unit is about where they spend their time, not who pays them.</Callout>
      </Section>

      {/* Office */}
      <Section title="Office" subtitle="Physical location" field="office" color="green">
        <p>
          The <strong>physical location</strong> where the person is based.
        </p>
        <Values
          items={[
            { value: "PARIS", desc: "Paris office (France)" },
            { value: "AMSTERDAM", desc: "Amsterdam office (Netherlands)" },
            { value: "BARCELONE", desc: "Barcelona office (Spain)" },
            { value: "TIRANA", desc: "Tirana office (Albania)" },
            { value: "REMOTE", desc: "Working remotely" },
          ]}
        />
        <h4 className="font-medium text-gray-700 mt-4 mb-2">Used for:</h4>
        <ul className="list-disc list-inside text-sm text-gray-600 space-y-1">
          <li>Office presence policies</li>
          <li>Team visualisation by location</li>
        </ul>
      </Section>

      {/* How they relate */}
      <div className="mt-10 bg-gray-50 rounded-lg border border-gray-200 p-6">
        <h2 className="text-lg font-bold text-gray-900 mb-4">How they relate</h2>
        <p className="text-sm text-gray-600 mb-4">
          Each user has all three fields. They are <strong>independent axes</strong> — a person can have any combination:
        </p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-300">
                <th className="text-left py-2 px-3 font-medium text-gray-700">Example</th>
                <th className="text-left py-2 px-3 font-medium text-blue-700">Organisation</th>
                <th className="text-left py-2 px-3 font-medium text-purple-700">Business Unit</th>
                <th className="text-left py-2 px-3 font-medium text-green-700">Office</th>
              </tr>
            </thead>
            <tbody className="text-gray-600">
              <tr className="border-b border-gray-100">
                <td className="py-2 px-3">French consultant on Selego projects</td>
                <td className="py-2 px-3">Le Collectif</td>
                <td className="py-2 px-3">SELEGO</td>
                <td className="py-2 px-3">PARIS</td>
              </tr>
              <tr className="border-b border-gray-100">
                <td className="py-2 px-3">Dutch dev working on Walego product</td>
                <td className="py-2 px-3">Selego BV</td>
                <td className="py-2 px-3">WALEGO</td>
                <td className="py-2 px-3">AMSTERDAM</td>
              </tr>
              <tr className="border-b border-gray-100">
                <td className="py-2 px-3">Albanian dev on Tirana startup</td>
                <td className="py-2 px-3">Selego BV</td>
                <td className="py-2 px-3">TIRANA</td>
                <td className="py-2 px-3">TIRANA</td>
              </tr>
              <tr className="border-b border-gray-100">
                <td className="py-2 px-3">Spanish designer on Walego</td>
                <td className="py-2 px-3">Selegoloco</td>
                <td className="py-2 px-3">WALEGO</td>
                <td className="py-2 px-3">BARCELONE</td>
              </tr>
              <tr>
                <td className="py-2 px-3">Remote Nour managing across poles</td>
                <td className="py-2 px-3">Le Collectif</td>
                <td className="py-2 px-3">SELEGO</td>
                <td className="py-2 px-3">REMOTE</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// --- Sub-components ---

const COLORS = {
  blue: { bg: "bg-blue-50", border: "border-blue-200", title: "text-blue-900", badge: "bg-blue-100 text-blue-700" },
  purple: { bg: "bg-purple-50", border: "border-purple-200", title: "text-purple-900", badge: "bg-purple-100 text-purple-700" },
  green: { bg: "bg-green-50", border: "border-green-200", title: "text-green-900", badge: "bg-green-100 text-green-700" },
};

const Section = ({ title, subtitle, field, color, children }) => {
  const c = COLORS[color];
  return (
    <div className={`${c.bg} ${c.border} border rounded-lg p-6 mb-6`}>
      <div className="flex items-start justify-between mb-3">
        <div>
          <h2 className={`text-lg font-bold ${c.title}`}>{title}</h2>
          <p className="text-sm text-gray-500">{subtitle}</p>
        </div>
        <span className={`text-xs font-mono px-2 py-1 rounded ${c.badge}`}>{field}</span>
      </div>
      <div className="text-sm text-gray-700 space-y-2">{children}</div>
    </div>
  );
};

const Values = ({ items }) => (
  <div className="mt-3 space-y-1">
    {items.map((item) => (
      <div key={item.value} className="flex items-baseline gap-2">
        <span className="font-mono text-xs bg-white px-2 py-0.5 rounded border border-gray-200 font-medium">{item.value}</span>
        <span className="text-sm text-gray-500">{item.desc}</span>
      </div>
    ))}
  </div>
);

const Callout = ({ children }) => (
  <div className="mt-4 bg-white/60 border-l-4 border-gray-300 pl-4 py-2 text-sm text-gray-600 italic">{children}</div>
);

export default Documentation;
